import React, { useEffect, useMemo, useState } from 'react';
import useOrganization from '../../../hooks/use-organization';

import { PaginationTable } from '../../../components/Redesing/table/pagination-table';

import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import {
  useSystemsRelayQuery,
  SystemQueryFilter,
  SystemSortInput,
} from '../../../hooks/system/query/V2/useSystemsRelay';
import { useKeysetPagination } from '../../../hooks/use-keyset-pagination';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { Stack } from '@mui/system';
import Card from '@mui/material/Card';
import PageContainer from '../../../components/Redesing/page-container';
import { ISystem } from '../../../ts/interface/system.interface';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import { addRemoveSystemToOrganization } from '../../../api/organization';
import { useDispatch, useSelector } from 'react-redux';
import { history } from '../../../utils';
import {
  clearSystemData,
  setNewSystem,
  systemLoaded,
} from '../../../actionCreators/system';
import { useOrganizationStore } from '../../../store/organizationStore';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import useSystems from '../../../hooks/system/query/useSystems';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import { OrganizationSystemColumns } from '../../../tableColumns/systemColumns';
import { MovaticFieldGroupAsyncSelect } from '../../../components/Redesing/movatic-field-group';
import { useUpdateEffect } from '../../../hooks/use-update-effect';
import { Option } from '../../../ts/interface/components/table.interface';
import CustomToast from '../../../components/Redesing/custom-toast';

function OrganizationSystemTable() {
  const organizationInfo = useOrganization();
  const dispatch = useDispatch<any>();
  const auth = useSelector((state: any) => state.admin.admin);

  // Switch into the clicked system's context: load it, drop the org context,
  // and navigate to its dashboard (same flow as the system selection modal).
  const goToSystem = (systemId: string) => {
    if (!systemId) return;
    dispatch(systemLoaded(false));
    dispatch(clearSystemData());
    Promise.resolve(dispatch(setNewSystem(systemId, auth))).then(() => {
      dispatch(systemLoaded(true));
      useOrganizationStore
        .getState()
        .setOrganizationInfo({ id: null, name: null, access: 0 });
      localStorage.removeItem('organizationId');
      history.push(`/dashboard?systemId=${systemId}`);
    });
  };

  const [confirm, setConfirm] = useState(false);
  const [addSystemModalOpen, setAddSystemModalOpen] = useState(false);
  const [searchRate, setSearchRate] = useState('');
  const [rowsPerPageRate, setRowsPerPageRate] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortConfig, setSortConfig] = useState<ISortConfig>({
    key: 'name',
    direction: 'asc',
  });
  const [selectedSystem, setSelectedSystem] = useState<ISystem | null>(null);
  const [openSystemDropdown, setOpenSystemDropdown] = useState(false);
  const [systemSearch, setSystemSearch] = useState('');
  const [selectedSystemFilter, setSelectedSystemFilter] = useState<Option | null>(null);

  // Systems available to add to the org (dropdown source).
  const debouncedSearchTerm = useSearchDebounce(systemSearch);
  const {
    data,
    refetch: refetchAvailable,
    isLoading: isLoadingAvailable,
  } = useSystems(
    {},
    {
      limit: 25,
      search: debouncedSearchTerm,
      isOrganizationSet: Boolean(organizationInfo.id),
    }
  );
  const allSystems = useMemo(
    () => (data as { systems?: ISystem[] })?.systems || [],
    [data]
  );

  useUpdateEffect(() => {
    refetchAvailable();
  }, [debouncedSearchTerm, addSystemModalOpen]);

  // Org systems table — GraphQL fetch with server-side search + keyset
  // pagination (scoped to this organization).
  const debouncedTableSearch = useSearchDebounce(searchRate);
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const tableFilter = useMemo<SystemQueryFilter>(
    () => ({
      organizationIds: organizationInfo.id ? [organizationInfo.id] : undefined,
      search: debouncedTableSearch || undefined,
    }),
    [organizationInfo.id, debouncedTableSearch]
  );

  const tableSort = useMemo<SystemSortInput[]>(
    () => [
      {
        field: sortConfig.key === 'status' ? 'STATUS' : 'NAME',
        order: sortConfig.direction === 'desc' ? 'DESC' : 'ASC',
      },
    ],
    [sortConfig.key, sortConfig.direction]
  );

  const {
    data: systemsData,
    count,
    hasMore,
    endCursor,
    isLoading,
    isRefetching,
    refetch: refetchSystems,
  } = useSystemsRelayQuery(tableFilter, {
    limit: rowsPerPageRate,
    after,
    sort: tableSort,
    enabled: Boolean(organizationInfo.id),
  });

  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemsData]);

  // The table columns/actions expect a `gid`; GraphQL exposes it as `id`.
  const systemItems = useMemo(
    () => (systemsData ?? []).map((system) => ({ ...system, gid: system.id })),
    [systemsData]
  );

  const addSystem = async (systemId: string) => {
    try {
      await addRemoveSystemToOrganization(systemId, organizationInfo.id, 'POST');
      CustomToast({
        type: 'success',
        message: 'System added to organization.',
      });
      resetPagination();
      refetchSystems();
      refetchAvailable();
      setAddSystemModalOpen(false);
      setSelectedSystemFilter(null);
    } catch (error) {
      CustomToast({
        type: 'error',
        message: 'Error adding system to organization. Please try again.',
      });
    }
  };

  const removeSystem = async (systemId: string) => {
    try {
      await addRemoveSystemToOrganization(systemId, organizationInfo.id, 'DELETE');
      CustomToast({
        type: 'success',
        message: 'System removed from organization.',
      });
      resetPagination();
      refetchSystems();
      refetchAvailable();
      setConfirm(false);
    } catch (error) {
      CustomToast({
        type: 'error',
        message: 'Error removing system from organization. Please try again.',
      });
    }
  };

  const confirmationModal = () => {
    return (
      <>
        <MovaticCustomModal
          title={'Remove System'}
          open={confirm}
          onClose={() => setConfirm(!confirm)}
          content={
            <div>
              Are you sure you would like to remove <b>{selectedSystem?.name}</b> from the
              organization <b>{organizationInfo.name}</b>?
            </div>
          }
          action={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => setConfirm(!confirm),
                },
                {
                  label: 'Remove',
                  onClick: () => removeSystem(selectedSystem?.gid || ''),
                },
              ]}
            />
          }
        />
      </>
    );
  };

  const addSystemModal = () => {
    return (
      <>
        <MovaticCustomModal
          title={'Add System'}
          open={addSystemModalOpen}
          onClose={() => setAddSystemModalOpen(!addSystemModalOpen)}
          content={
            <>
              <MovaticFieldGroupAsyncSelect
                dataId="location-single-select"
                label={'System'}
                description="Select the system to add to the organization."
                isLoading={isLoadingAvailable}
                name={'system_select'}
                id={'system_select'}
                options={allSystems.map((system: ISystem) => ({
                  value: system.gid,
                  label: system.name,
                }))}
                showClearButton={true}
                value={selectedSystemFilter}
                onChange={(
                  event: React.SyntheticEvent,
                  newValue: { value: string; label: string }
                ) => {
                  setSelectedSystemFilter(newValue);
                }}
                open={openSystemDropdown}
                onOpen={() => {
                  setOpenSystemDropdown(true);
                }}
                onClose={() => {
                  setOpenSystemDropdown(false);
                }}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.value === value.value
                }
                getOptionText={(option: any) => option.label}
                handleSearch={(search) => setSystemSearch(search.target.value)}
              />
            </>
          }
          action={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => setAddSystemModalOpen(!addSystemModalOpen),
                },
                {
                  label: 'Add',
                  onClick: () => addSystem(selectedSystemFilter?.value || ''),
                },
              ]}
            />
          }
        />
      </>
    );
  };

  return (
    <PageContainer spaceName={'Systems'}>
      <Card>
        <Stack spacing={1} py={2}>
          <PaginationTable
            padding={{ pt: 1, pl: 0, pr: 1 }}
            isLoading={isLoading || isPaginating}
            isLoadingCache={isRefetching}
            localSearch={false}
            noDataSlice
            buttons={[
              {
                label: '',
                disabled: true,
                onClick: () => {
                  setAddSystemModalOpen(true);
                },
                startIcon: <></>,
                variant: 'contained',
              },
            ]}
            showFilter={false}
            onRowCLick={(id: string) => goToSystem(id)}
            sortBy={sortConfig.key}
            sortDirection={sortConfig.direction}
            onSortChange={(sort) => {
              resetPagination();
              setSortConfig(sort as ISortConfig);
            }}
            items={systemItems}
            totalItems={count}
            hasMore={hasMore}
            noDataText={'This organization has no systems at this time'}
            columns={
              OrganizationSystemColumns(setSelectedSystem, setConfirm) as IColumn[]
            }
            rowsPerPage={rowsPerPageRate}
            page={pageNumber}
            onPageChange={(page, pageN: number) => onPageChange(pageN)}
            onRowsPerPageChange={(rowsPerPage) => {
              resetPagination();
              setRowsPerPageRate(Number(rowsPerPage.target.value));
            }}
            searchText={searchRate}
            searchPlaceholder={'Search systems'}
            handleSearch={(search) => {
              resetPagination();
              setSearchRate(search.target.value);
            }}
          />
        </Stack>
      </Card>
      {addSystemModal()}
      {confirmationModal()}
    </PageContainer>
  );
}

export default OrganizationSystemTable;
